/* eslint-disable camelcase */
import { Button, SelectInput, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { enumToArray, generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { BackButton } from '../../components/buttons/back-button';
import { useRoutePaths } from '../../../../shared/context/paths';
import { routePaths } from '../../routes';
import {
  DeviceType,
  HeatingControlHeatPump,
  HeatingControlRelay,
  HeatingSystem,
  RoomDialWrf06,
  RoomSensorNovos3,
  RoomSensorRegin,
  RoomSensorWrf04,
  RoomSensorWrf06,
  RuleActionType,
  RuleCategory,
} from '../../../../shared/api-clients/_api/index';
import { useBeeConfig } from '../../hooks/beeConfig';
import { Spinner } from '../../components/spinner/spinner';

interface IHeatingSystemTypeForm {
  heatingSystem: HeatingSystem;
}

export const validators: InputValidator<IHeatingSystemTypeForm>[] = [
  {
    key: 'heatingSystem',
    validators: [
      {
        validate: value => enumToArray(HeatingSystem).includes(value),
        message: 'Must be a valid heating system type',
      },
    ],
  },
];

const initialState: IHeatingSystemTypeForm = {
  heatingSystem: null,
};

type SensorDevice = RoomSensorWrf06 | RoomSensorWrf04 | RoomSensorRegin | RoomSensorNovos3;

export const HeatingSystemTypeView: React.FunctionComponent = () => {
  const { push } = useRoutePaths();
  const { getAssignedResourcesFor, getDevicesByType, removeDevice, editDevice, loading } = useBeeConfig();

  const currentlyConfiguredHeatingSystem: HeatingSystem = getAssignedResourcesFor(
    [DeviceType.RELAY, DeviceType.MASTERTHERM],
    'heating_system',
    null
  )[0];

  const initState: IHeatingSystemTypeForm = React.useMemo(() => {
    return currentlyConfiguredHeatingSystem?.length > 1
      ? {
          heatingSystem: currentlyConfiguredHeatingSystem,
        }
      : initialState;
  }, [currentlyConfiguredHeatingSystem]);

  const { DataForm, bind, dataBinder } = useForm(initState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IHeatingSystemTypeForm>(
    validators,
    dataBinder
  );

  const unassignDialOrSensorFromZone = <T extends SensorDevice>(device: T) => {
    const {
      deviceType,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      params: { action_type },
    } = device;

    if (
      deviceType === DeviceType.WRF04 ||
      deviceType === DeviceType.REGIN ||
      deviceType === DeviceType.NOVOS3 ||
      (deviceType === DeviceType.WRF06 && action_type === RuleActionType.MONITORING)
    ) {
      editDevice(device, {
        ...device,
        params: { ...device.params, primary_zone_temperature_sensor: 0, zone_id: null },
      });
    } else {
      editDevice(device, {
        ...device,
        params: { ...device.params, zone_id: null },
      });
    }
  };

  const removeBoilerHeatingZones = () => {
    const assignedRelays = getDevicesByType<HeatingControlRelay>([DeviceType.RELAY]).filter(
      ({ params }) => params.zone_id
    );

    const assignedDials = getDevicesByType<RoomDialWrf06>([DeviceType.WRF06]).filter(
      ({ params: { zone_id, action_type } }) => zone_id && action_type === RuleActionType.CONTROL
    );

    const assignedWrf06TempSensors = getDevicesByType<RoomSensorWrf06>([DeviceType.WRF06]).filter(
      ({ params: { zone_id, action_type } }) => zone_id && action_type === RuleActionType.MONITORING
    );

    const otherAssignedTempSensors = getDevicesByType<RoomSensorWrf04 | RoomSensorRegin | RoomSensorNovos3>([
      DeviceType.WRF04,
      DeviceType.REGIN,
      DeviceType.NOVOS3,
    ]).filter(({ params: { zone_id } }) => zone_id);

    const assignedSensors = [...assignedWrf06TempSensors, ...otherAssignedTempSensors];

    assignedRelays.map(relay => removeDevice(relay));

    assignedDials.map(dial => unassignDialOrSensorFromZone(dial));

    assignedSensors.map(sensor => unassignDialOrSensorFromZone(sensor));
  };

  const removeHeatPumpHeatingZones = () => {
    const assignedHeatPumps = getDevicesByType<HeatingControlHeatPump>([DeviceType.MASTERTHERM]).filter(
      ({ params: { zone_id, category } }) => zone_id && category === RuleCategory.HEATING
    );

    const assignedDials = getDevicesByType<RoomDialWrf06>([DeviceType.WRF06]).filter(
      ({ params: { zone_id, action_type } }) => zone_id && action_type === RuleActionType.CONTROL
    );

    const assignedDialsMonitoring = getDevicesByType<RoomSensorWrf06>([DeviceType.WRF06]).filter(
      ({ params: { zone_id, action_type } }) => zone_id && action_type === RuleActionType.MONITORING
    );

    assignedHeatPumps.map(heatPump => removeDevice(heatPump));

    assignedDials.map(dial => unassignDialOrSensorFromZone(dial));

    assignedDialsMonitoring.map(dialMonitoring => unassignDialOrSensorFromZone(dialMonitoring));
  };

  const handleEditHeatingSystem = async (currentHeatingSystem, newHeatingSystem) => {
    const isChangeFromBoilerToHeatPump =
      newHeatingSystem === HeatingSystem.MASTERTHERM &&
      (currentHeatingSystem === HeatingSystem.HOTWATER_CYLINDER || currentHeatingSystem === HeatingSystem.COMBI);

    const isChangeFromBoilerToNotControlled =
      newHeatingSystem === HeatingSystem.NOT_CONTROLLED_BY_IES &&
      (currentHeatingSystem === HeatingSystem.HOTWATER_CYLINDER || currentHeatingSystem === HeatingSystem.COMBI);

    const isChangeFromHeatPumpToBoiler =
      (newHeatingSystem === HeatingSystem.HOTWATER_CYLINDER || newHeatingSystem === HeatingSystem.COMBI) &&
      currentHeatingSystem === HeatingSystem.MASTERTHERM;

    const isChangeFromHeatPumpToNotControlled =
      newHeatingSystem === HeatingSystem.NOT_CONTROLLED_BY_IES && currentHeatingSystem === HeatingSystem.MASTERTHERM;

    if (isChangeFromBoilerToHeatPump || isChangeFromBoilerToNotControlled) removeBoilerHeatingZones();

    if (isChangeFromHeatPumpToBoiler || isChangeFromHeatPumpToNotControlled) removeHeatPumpHeatingZones();
  };

  const handleContinue = () => {
    const heatingSystem = dataBinder.getValue('heatingSystem');
    const heatingSystemType = Object.entries(HeatingSystem)
      .find(([, value]) => value === heatingSystem)[0]
      .toLowerCase();

    const newlySelectedHeatingSystem = heatingSystem;

    const hasHeatingSystemChanged = newlySelectedHeatingSystem !== currentlyConfiguredHeatingSystem;

    if (currentlyConfiguredHeatingSystem && hasHeatingSystemChanged) {
      handleEditHeatingSystem(currentlyConfiguredHeatingSystem, newlySelectedHeatingSystem);
    }

    switch (heatingSystem) {
      case HeatingSystem.COMBI:
      case HeatingSystem.HOTWATER_CYLINDER:
        push(routePaths.heatingSystem.assignBoilerHeatingZones.root, { boilerType: heatingSystemType });
        break;
      case HeatingSystem.MASTERTHERM:
        push(routePaths.heatingSystem.assignHeatPumpHeatingZones.root, { heatPumpType: heatingSystemType });
        break;
      default:
        push(routePaths.verify.firmware.check);
        break;
    }
  };

  const options = generateSelectOptions(HeatingSystem);

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>Heating system</h1>

      <div>
        <SelectInput
          {...bind.select('heatingSystem')}
          label="What type of heating system is installed at the property?"
          options={options}
          onBlur={setTouched}
          data-qa="select-heating-system"
        />
      </div>

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          disabled={formIsValid === false}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          Continue
        </Button>
      </div>
    </DataForm>
  );
};
